/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {
  allowedPageViewModes,
  allowedDocViewModesForApi,
  explicitlyDisableFeatures,
  allowedEventsToBeTriggeredByOverlay,
  validThemes
} from "publicApis/constants";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { isNumber, isBoolean } from "utils/APIUtils";
import {
  isExplicitlyDisabledFeature,
  isZoomFeatureEnabled,
  isSearchFeatureEnabled,
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer
} from "selectors/appSelectors";
import { dialogApi } from "components/dialogs";
import { getSearchManager, getFirstSearchResultPromiseObj } from "selectors/searchSelectors";
import { getNewSearchAPIObj } from "publicApis/SearchAPI";
import { getCurrentPageZoomLevel, getMinZoom, getMaxZoom, getPageZoomLevel } from "selectors/zoom";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { enableSearchUI } from "selectors/searchSelectors";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  docViewModes,
  EVENT_NAMES,
  appIdentifiers,
  fullScreenExitReason,
  searchAddOnPanelTrigger,
  AJSMetadataStatus
} from "constants/enums";
import { getViewMode } from "selectors/viewModeSelectors";
import { getActionsUsingBookmarkId } from "selectors/bookmarksSelectors";
import { rotatePageHandler, fitOneFullPageHandler, fullScreenModeHandler } from "services/NavigationBarService";
import {
  getViewRotationAngle,
  isTwoColumnViewMode,
  isFullScreenMode,
  getAJSMetadataStatus
} from "selectors/documentSelectors";
import { performPdfActions } from "pdfActionManager";
import docApi from "api";
import { previewEvents } from "publicApis/Events/previewEvents";
import { _isRotateViewEnabled } from "../selectors/hudSelectors";
import { getContentTypeUnderMouse, pageContentTypesReverseMap } from "../pageContentHandler/HitTester";
import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { executeDocWillCloseActions } from "pdfActionManager/docActionsHelper";
import { getPagesViewBoundingRect } from "selectors/documentViewSelectors";
import { exitFullScreenView } from "utils/PageByPageViewUtils";
import { getTotalPages } from "selectors/documentSelectors";
import { getPageTextLoadStatus, getPageHandle } from "selectors/pageSelectors";
import { loadStatus } from "constants/enums";
import { ACCESSIBILITY_TEXT_EXTRACT_PRIORITY, NOTIFICATION_CLOSE_REASON } from "constants/constants";
import { getBottomPanelHeight } from "../selectors/documentSelectors";
import { SEARCH_HIGH_PRIORITY } from "../constants/constants";
import { ajsMetadataDonePromise } from "utils/DocumentViewUtils";
import { recordCNPDFSwitchBegin, recordCNPDFSwitchEnd } from "../utils/performance";
import { logCNPDFComponentFetchLogs } from "../utils/CnpdfLoggingUtils";
import { overlayEvents } from "overlays/OverlayEvents";

export const getDocumentActionApis = stores => {
  function setViewMode(viewMode) {
    if (getViewMode(stores) === docViewModes.NO_PREVIEW || getViewMode(stores) === docViewModes.UNKNOWN) return;

    stores.documentStore.toggleViewWithScrolling(true);
    if (allowedPageViewModes.hasOwnProperty(viewMode)) {
      if (isExplicitlyDisabledFeature(stores, explicitlyDisableFeatures.ZOOM)) {
        return;
      }
      const newPageViewModifier = allowedPageViewModes[viewMode];
      if (!(getViewMode(stores) === docViewModes.CLASSIC_VIEW)) {
        stores.documentStore.setViewMode(docViewModes.CLASSIC_VIEW);
      }
      stores.zoom.setPageViewModifier({ newPageViewModifier });
    } else if (allowedDocViewModesForApi.hasOwnProperty(viewMode)) {
      if (viewMode === appIdentifiers.FIT_ONE_FULL_PAGE) {
        stores.pageByPageStore.setEnableFitOneFullPageMode(true);
        fitOneFullPageHandler();
      } else if (viewMode === appIdentifiers.FULL_SCREEN) {
        fullScreenModeHandler();
      } else {
        stores.documentStore.setViewMode(allowedDocViewModesForApi[viewMode]);
      }
    }
  }

  function setCoverPageMode(status) {
    if (isBoolean(status) && isTwoColumnViewMode(stores)) {
      stores.documentStore.toggleCoverMode(status);
    }
  }

  function goto(pageNumber, x = 0, y = 0, scrollOptions) {
    if (isNumber(pageNumber)) {
      initialPageLoadedPromise(stores)
        .then(() => {
          stores.pagedViewState.goToLocation(pageNumber - 1, x, y, -1, scrollOptions);
        })
        .catch(() => {});
    }
  }

  function addBottomSpaceBelowDocumentInMobile(space, opts = {}) {
    if (!getShouldUseMobileModernViewer(stores)) return;

    const { excludeStickyToolHeight = false } = opts;
    if (excludeStickyToolHeight) {
      const bottomPanelHeight = getBottomPanelHeight(stores) || 0;
      stores.documentStore.setDocumentBottomSpace(space - bottomPanelHeight);
    } else {
      stores.documentStore.setDocumentBottomSpace(space);
    }
  }

  function getDialogBox() {
    return Promise.resolve({
      showDialog: dialogApi.show,
      hideDialog: dialogApi.hide,
      setDialogHasError: dialogApi.setDialogHasError
    });
  }
  function search(text) {
    return new Promise((resolve, reject) => {
      if (!isSearchFeatureEnabled(stores)) return reject("SEARCH_FEATURE_DISABLED");
      stores.searchStore.setActiveSearchAPIObj(null);
      stores.searchStore.setNewFirstSearchResultPromise();
      initialPageLoadedPromise(stores)
        .then(() => {
          let searchManager = getSearchManager(stores);
          if (searchManager) {
            searchManager.clearSearch();
            searchManager.onNextClicked(text);
          } else {
            return reject("SEARCH_FEATURE_DISABLED");
          }
          getFirstSearchResultPromiseObj(stores)
            .promise.then(() => {
              getFirstSearchResultPromiseObj(stores).done = true;
              let searchAPIobj = getNewSearchAPIObj(stores);
              stores.searchStore.setActiveSearchAPIObj(searchAPIobj);
              resolve(searchAPIobj);
            })
            .catch(() => {
              getFirstSearchResultPromiseObj(stores).done = true;
              reject("NO_RESULT_FOUND");
            });
        })
        .catch(() => {
          reject("ERROR");
        });
    });
  }

  function zoomTo(zoomPercent) {
    if (!isInitialPageLoaded(stores) || !isZoomFeatureEnabled(stores)) {
      return -1;
    }
    if (isNumber(zoomPercent) && zoomPercent > 0) {
      stores.zoom.performZoomAction({ newZoomLevel: zoomPercent / 100 });
      return getCurrentPageZoomLevel(stores) * 100;
    }
    return -1;
  }

  function zoomIn() {
    if (!isInitialPageLoaded(stores) || !isZoomFeatureEnabled(stores)) {
      return -1;
    }
    stores.zoom.zoomIn();
    return getCurrentPageZoomLevel(stores) * 100;
  }

  function zoomOut() {
    if (!isInitialPageLoaded(stores) || !isZoomFeatureEnabled(stores)) {
      return -1;
    }
    stores.zoom.zoomOut();
    return getCurrentPageZoomLevel(stores) * 100;
  }

  function getZoomLimits() {
    return new Promise((resolve, reject) => {
      if (isZoomFeatureEnabled(stores)) {
        initialPageLoadedPromise(stores)
          .then(() => {
            resolve({
              minZoom: (getMinZoom(stores) / getDevicePixelRatio()) * 100,
              maxZoom: (getMaxZoom(stores) / getDevicePixelRatio()) * 100
            });
          })
          .catch(() => {
            reject("ERROR");
          });
      } else {
        reject("ZOOM_FEATURE_DISABLED");
      }
    });
  }

  function setPdfEdited(args) {
    docApi._pdfEditedInEditMode(args);
  }

  function textSelectionFromPDF(args) {
    return docApi.textSelectionFromPDFCoordinates(
      args.startPoint,
      args.endPoint,
      args.priority,
      args.snapToWords,
      args.getCaret,
      args.force
    );
  }

  function showSearchInputUI() {
    if (enableSearchUI(stores)) {
      stores.searchStore.setShowSearchInputUI(true, getViewMode(stores));
    }
  }

  function hideSearchInputUI() {
    getSearchManager(stores).closeSearchUI();
    stores.searchStore.setShowSearchInputUI(false);
  }

  function executeBookmark(id) {
    const actions = getActionsUsingBookmarkId(stores, id);
    if (actions && actions[EVENT_NAMES.MOUSE_UP]) {
      return performPdfActions(stores, actions[EVENT_NAMES.MOUSE_UP], { triggeredFrom: "bookmarks" });
    }
    return { success: true };
  }

  function executePdfActions(action, extraParams = {}) {
    return performPdfActions(stores, action, extraParams);
  }

  function setOCGLayerVisibility(ocgName, isVisible) {
    return stores.documentStore.setOCGLayerVisibility(ocgName, isVisible);
  }

  function resetRotateView() {
    return _rotateView(-getViewRotationAngle(stores));
  }

  function rotateViewClockwise() {
    return _rotateView(90);
  }

  function rotateViewAntiClockwise() {
    return _rotateView(270);
  }

  async function _rotateView(rotation) {
    if (!isInitialPageLoaded(stores)) {
      return -1;
    }
    if (_isRotateViewEnabled(stores)) {
      await rotatePageHandler(rotation);
      return getViewRotationAngle(stores);
    }
    return -1;
  }

  function triggerPreviewEvent(eventName, eventData) {
    if (allowedEventsToBeTriggeredByOverlay.includes(eventName)) {
      previewEvents.trigger(eventName, eventData);
    } else {
      throw new Error("Can't trigger event");
    }
  }

  function hitTest(xCoord, yCoord, pageIndex) {
    if (!isInitialPageLoaded(stores)) {
      return -1;
    }
    return new Promise((resolve, reject) => {
      const { x, y } = transformViewToPDF(xCoord, yCoord, pageIndex, stores);
      getContentTypeUnderMouse(pageIndex, x, y, getPageZoomLevel(stores, pageIndex)).then(contentType => {
        const hitTestResult = {
          pageNumber: pageIndex + 1,
          x: xCoord,
          y: yCoord,
          contentType: pageContentTypesReverseMap[contentType]
        };
        resolve(hitTestResult);
      });
    });
  }

  function showNotification(
    msg,
    opts = {
      overrideClassName: null,
      shouldRemoveActiveToasts: false,
      shouldRemoveActiveBanners: false,
      onClose: () => {},
      timeout: 0,
      type: null,
      variant: null,
      actionLabel: null,
      onAction: () => {},
      messageIcon: null
    }
  ) {
    if (!msg) {
      throw new Error("Message is required!");
    }
    if (opts && opts.timeout && !isNumber(opts.timeout)) {
      throw new Error("Timout value must be a number!");
    }
    const notificationId = stores.notificationViewStore.addNotification(msg, opts);
    return notificationId;
  }

  function showToastNotification(msg, opts = { timeout: 0, shouldRemoveActiveToasts: false, onClose: () => {} }) {
    if (!msg) {
      throw new Error("Message is required!");
    }
    if (opts && opts.timeout && !isNumber(opts.timeout)) {
      throw new Error("Timout value must be a number!");
    }
    console.error(`showToastNotification() => This API is Deprecated! Use showNotification() API instead`);

    stores.notificationViewStore.addNotification(msg, opts);
  }

  function clearAllNotifications() {
    stores.notificationViewStore.removeAllNotifications();
  }

  function clearNotification(id) {
    return stores.notificationViewStore.removeNotification(id, NOTIFICATION_CLOSE_REASON.API_CALL);
  }

  function setDarkPageThemeSwitchDisabled(isDisabled) {
    stores.pageThemeSwitcherStore.setDarkPageThemeSwitchDisabled(isDisabled);
  }

  function setDocumentViewThemeChange(themeData) {
    if (isBoolean(themeData.isDarkPageThemeEnabled)) {
      stores.pageThemeSwitcherStore.setDarkPageTheme(themeData.isDarkPageThemeEnabled);
    }
    if (validThemes.includes(themeData.docViewTheme)) {
      stores.pageThemeSwitcherStore.setDocViewTheme(themeData.docViewTheme);
    }
  }

  function exitFullScreenMode() {
    if (isFullScreenMode(stores)) {
      exitFullScreenView(stores, fullScreenExitReason.PUBLIC_API);
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }

  function undo() {
    const messageObj = {
      command: "undo",
      params: {}
    };
    return docApi._dispatch(messageObj).then(data => data);
  }

  function redo() {
    const messageObj = {
      command: "redo",
      params: {}
    };
    return docApi._dispatch(messageObj).then(data => data);
  }

  function registerSearchAddOnPanel({ callback, getTripleDotMenuItems, trigger = searchAddOnPanelTrigger.ON_FOCUS }) {
    stores.searchStore.registerSearchAddOnPanel({ callback, getTripleDotMenuItems, trigger });
    return true;
  }

  function registerBeforeA11yTreeRenderCallback(callback) {
    stores.accessibilityStore.setBeforeA11yTreeRenderCallback(callback);
    const pageCount = getTotalPages(stores);
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const isPageLoaded = getPageTextLoadStatus(stores, pageIndex) === loadStatus.LOADED;
      if (isPageLoaded) {
        stores.documentStore.unloadPageAccData(pageIndex);
        stores.documentStore.loadPageAccText(pageIndex, ACCESSIBILITY_TEXT_EXTRACT_PRIORITY);
      }
    }
  }

  function getSearchResults(text, startPage, numPages) {
    let searchResult = docApi.performSearch(text, startPage, numPages, SEARCH_HIGH_PRIORITY, 1);
    return searchResult;
  }

  async function switchToCNPDF(cnpdfFetcher) {
    await ajsMetadataDonePromise(stores);
    // Switch to CNPDF only after ajsMetadata successfully fetched via PDF
    recordCNPDFSwitchBegin();

    let switchResult;
    try {
      switchResult = await docApi.switchToCNPDF(cnpdfFetcher);
      logCNPDFComponentFetchLogs(switchResult);
      stores.appStore.setEnableCNPDF(true);
      const totPages = getTotalPages(stores);
      await stores.documentStore.reloadPagesAndGeometries(1, totPages, {
        refreshGeometry: false
      });
    } catch (error) {
      stores.appStore.setEnableCNPDF(false);
      switchResult = Promise.reject(error);
    } finally {
      recordCNPDFSwitchEnd();
    }

    return switchResult;
  }

  function notifyAssetVersionUpdate(event) {
    overlayEvents.trigger("assetVersionUpdated", event);
  }

  // TODO: @prkuma - remove this api once the corresponding edit api for conflict resolution is merged
  async function resolveConflicts(params) {
    return;
  }

  return {
    setViewMode,
    setCoverPageMode,
    goto,
    getDialogBox,
    zoomTo,
    zoomIn,
    zoomOut,
    getZoomLimits,
    search,
    setPdfEdited,
    showSearchInputUI,
    hideSearchInputUI,
    registerSearchAddOnPanel,
    executeBookmark,
    setOCGLayerVisibility,
    resetRotateView,
    rotateViewClockwise,
    rotateViewAntiClockwise,
    triggerPreviewEvent,
    executePdfActions,
    hitTest,
    showNotification,
    showToastNotification,
    clearAllNotifications,
    clearNotification,
    undo,
    redo,
    executeDocWillCloseActions: () => executeDocWillCloseActions(stores),
    exitFullScreenMode,
    setDarkPageThemeSwitchDisabled,
    setDocumentViewThemeChange,
    registerBeforeA11yTreeRenderCallback,
    getImageBuffer: docApi.getImageBuffer.bind(docApi),
    getImageC2PAMetadata: docApi.getImageC2PAMetadata.bind(docApi),
    textSelectionFromPDF,
    addBottomSpaceBelowDocumentInMobile,
    getSearchResults,
    switchToCNPDF,
    notifyAssetVersionUpdate,
    resolveConflicts
  };
};
